import { formatPercent, EMPTY } from '@pmai/contracts';
import { cn } from './cn';

/** Completion bar with the percentage (FR-DASH-010, FR-PRJ-012); `null` → "–". */
export function ProgressBar({ value, label }: { value: number | null; label?: string }) {
  if (value === null) return <span className="text-ink-muted">{EMPTY}</span>;
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      className="flex min-w-24 items-center gap-2"
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className={cn('h-full rounded-full', pct >= 100 ? 'bg-emerald-500' : 'bg-brand-500')}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-10 text-right text-xs tabular-nums text-ink-muted">{formatPercent(value)}</span>
    </div>
  );
}

export type BarTone = 'green' | 'yellow' | 'red' | 'grey';

const BAR_TONES: Record<BarTone, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-400',
  red: 'bg-red-500',
  grey: 'bg-slate-300',
};

/**
 * A coloured bar without a number (project indicators, FR-PRJ-017). `value` is clamped to
 * 0–100; `over` marks a value above 100 % (FR-PRJ-021).
 */
export function ToneBar({
  value,
  tone,
  label,
  over,
}: {
  value: number;
  tone: BarTone;
  label: string;
  over?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      data-tone={tone}
      className={cn('h-2 overflow-hidden rounded-full bg-slate-100', over && 'ring-2 ring-red-300')}
    >
      <div className={cn('h-full rounded-full', BAR_TONES[tone])} style={{ width: `${pct}%` }} />
    </div>
  );
}
